import axios from 'axios'
import { message } from 'antd'
import { API_URL, TOKEN_KEY } from '@/config/api'
import { errorTracker } from '@/utils/errorTracker'
import { leggiStatoSimulazione, azzeraStatoSimulazione } from '@/lib/statoSimulazione'

// Crea istanza axios
const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

// Durante la simulazione di un ruolo l'applicazione va in sola lettura: la
// simulazione vive solo nel browser, mentre il backend continua ad autorizzare
// sull'utente vero. Senza questo blocco un'azione mostrata dalla UI simulata
// verrebbe eseguita davvero con i permessi di chi sta simulando.
const METODI_DI_SCRITTURA = ['post', 'put', 'patch', 'delete']

// Due eccezioni, per ragioni diverse.
//
// 1. Il LOGIN non va bloccato mai, in nessuna condizione. E' una scrittura, ma
//    e' la scrittura che permette di uscire da qualunque stato: bloccarlo
//    chiude l'utente fuori dalla propria applicazione. Era successo davvero —
//    uscendo dall'account con la simulazione accesa, lo stato restava nel
//    browser (logout naviga senza ricaricare la pagina) e il login successivo
//    veniva rifiutato; se ne usciva solo svuotando localStorage dagli
//    strumenti per sviluppatori. La simulazione ora si spegne al logout, ma
//    questa esclusione resta come rete: nessun bug futuro deve poter
//    trasformare la simulazione in una serratura.
// 2. La scrittura della matrice permessi, che serve al pannello diagnostico
//    per provare un permesso senza uscire dalla simulazione.
const eccezioneConsentita = (metodo: string, url: string): boolean =>
  url.includes('/auth/login') ||
  (metodo === 'put' && url.includes('/permissions/matrix'))

export interface ErroreSimulazione extends Error {
  simulazioneBloccata: true
  ruoloSimulato: string | null
}

// Request interceptor - aggiungi token
axiosInstance.interceptors.request.use(
  (config) => {
    const metodo = (config.method || 'get').toLowerCase()
    const simulazione = leggiStatoSimulazione()
    if (
      simulazione.attiva &&
      METODI_DI_SCRITTURA.includes(metodo) &&
      !eccezioneConsentita(metodo, config.url || '')
    ) {
      const errore = new Error('Azione bloccata dalla simulazione') as ErroreSimulazione
      errore.simulazioneBloccata = true
      errore.ruoloSimulato = simulazione.ruolo
      ;(errore as any).config = config
      return Promise.reject(errore)
    }

    const token = localStorage.getItem(TOKEN_KEY)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // DEBUG: Log per tutte le richieste che richiedono autenticazione
    if (config.url?.includes('users') || config.url?.includes('settings')) {
    }
    if (config.url?.includes('login')) {
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor - gestione errori
axiosInstance.interceptors.response.use(
  (response) => {
    if (response.config.url?.includes('login')) {
    }
    return response
  },
  (error) => {
    // Richiesta fermata dall'interceptor: messaggio dedicato, nessun tracking
    // e nessuno dei toast generici piu' sotto (non c'e' stata alcuna risposta).
    if (error?.simulazioneBloccata) {
      message.warning(
        `Azione bloccata: sei in simulazione come ${error.ruoloSimulato ?? 'altro ruolo'}. ` +
          'Esci dalla simulazione per agire.',
      )
      return Promise.reject(error)
    }

    if (error.config?.url?.includes('login')) {
      console.error('❌ Login error details:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers,
      })
    }
    
    const url = error.config?.url || 'unknown'
    const method = error.config?.method?.toUpperCase() || 'unknown'
    const status = error.response?.status

    // Endpoint che gestiscono i propri errori inline — l'interceptor non mostra toast
    const selfHandledUrls = [
      'login',              // errori di login gestiti da AuthContext/LandingPage, non dall'interceptor
      'register-request',
      'approve',
      'reject',
      'rdas/intervention',  // 404 atteso quando nessuna RDA esiste ancora
      'speech/',            // VoiceTextarea gestisce i propri errori inline
      'rdo-templates/match', // 404 atteso quando nessun template corrisponde
    ]
    // Le transizioni di stato RDA raccontano l'esito da sole, e meglio di qui:
    // OrdiniPage dice che gli articoli sono registrati ma l'ordine no,
    // RDAStateTransition apre una Modal col dettaglio del backend. Il toast
    // generico dell'interceptor si sovrapporrebbe e contraddirebbe — un
    // "Non hai i permessi" secco fa credere che non sia stato salvato niente.
    const isTransizioneRda = /\/purchase-requests\/\d+\/transition\//.test(
      error.config?.url || '',
    )
    const isSelfHandled =
      selfHandledUrls.some(u => error.config?.url?.includes(u)) || isTransizioneRda
    if (isSelfHandled) {
      return Promise.reject(error)
    }

    // 403 attesi su endpoint noti — nessun log, nessun toast
    const silent403Urls = ['status-cards', 'workshops']
    const isSilent403 = status === 403 && silent403Urls.some(u => error.config?.url?.includes(u))
    if (isSilent403) {
      return Promise.reject(error)
    }

    // Traccia l'errore nel sistema di debug
    errorTracker.trackAPIError(url, method, status, error, {
      data: error.response?.data,
      endpoint: url,
      method: method,
    })

    console.error('❌ API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
    })

    // Errore di rete - ma NON mostrare messaggio per alcuni endpoint non-critici
    if (!error.response) {
      // Url non-critiche che non devono mostrare errore di connessione
      const nonCriticalUrls = [
        'available-transitions',  // Caricamento transizioni
        'audit-trail',  // Caricamento audit trail
        'status-cards',  // Dashboard: 403 gestito inline nel componente
      ]

      const isNonCritical = nonCriticalUrls.some(url => error.config?.url?.includes(url))

      if (!isNonCritical) {
        message.error('Errore di connessione al server')
      }
      return Promise.reject(error)
    }

    const { status: statusCode, data } = error.response

    switch (statusCode) {
      case 400:
        message.error(data.detail || 'Richiesta non valida')
        break
      case 401:
        message.error('Sessione scaduta. Effettua nuovamente il login.')
        localStorage.removeItem(TOKEN_KEY)
        // Si esce dall'account: la simulazione non deve sopravvivere alla
        // sessione che l'aveva accesa. Qui basta lo stato di modulo, perche'
        // subito sotto si ricarica la pagina e il contesto React nasce pulito.
        azzeraStatoSimulazione()
        window.location.href = `/?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`
        break
      case 403:
        message.error('Non hai i permessi per eseguire questa operazione')
        break
      case 404:
        message.error('Risorsa non trovata')
        break
      case 422:
        // Validation errors
        if (data.detail && Array.isArray(data.detail)) {
          const errors = data.detail.map((err: any) => err.msg).join(', ')
          message.error(errors)
        } else {
          message.error(data.detail || 'Errore di validazione')
        }
        break
      case 500:
        message.error('Errore interno del server')
        break
      default:
        message.error(data.detail || 'Si è verificato un errore')
    }

    return Promise.reject(error)
  }
)

export default axiosInstance
